import { Link } from 'react-router-dom';
import { Footer } from '../components/layout/Footer';

const LAST_UPDATED = 'March 14, 2026';
const CONTACT_EMAIL = 'legal@vowvisionsai.com';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using VowVisionsAI ("Service", "we", "us", "our") at vowvisionsai.com, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you must not use the Service.

These Terms apply to all users worldwide, including visitors, registered users, and paying subscribers. By creating an account or using any feature of the Service, you confirm that you are at least 18 years old (or the age of legal majority in your jurisdiction) and have the legal capacity to enter into binding agreements.`
  },
  {
    id: 'service-description',
    title: '2. Description of Service',
    content: `VowVisionsAI is an AI-powered invitation design platform that helps users create, customise, preview, and export digital and print-ready wedding and event invitations. The Service includes:

• AI-generated invitation designs based on user input
• An invitation library and management dashboard
• Export functionality (PNG, PDF, story formats)
• Account management and subscription billing (via Stitch Express, when enabled)

We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time with reasonable notice.`
  },
  {
    id: 'accounts',
    title: '3. User Accounts',
    items: [
      'You must provide accurate and complete information when creating an account.',
      'You are responsible for maintaining the confidentiality of your account credentials.',
      'You are responsible for all activity that occurs under your account.',
      'You must notify us immediately at legal@vowvisionsai.com if you suspect unauthorised access.',
      'We reserve the right to suspend or terminate accounts that violate these Terms.',
      'You may not create accounts on behalf of others without their explicit consent.',
    ]
  },
  {
    id: 'subscriptions',
    title: '4. Subscriptions & Payments',
    subsections: [
      {
        subtitle: '4.1 Plans',
        content: `VowVisionsAI offers the following plans: Essential (free for first month, then $5/month), Signature ($5 promo / $8 regular per month), and Heirloom ($10 promo / $15 regular per month). Prices are displayed in USD and may vary by region.`
      },
      {
        subtitle: '4.2 Billing',
        content: `Subscriptions are billed monthly. Payment is processed via Stitch Express. By subscribing, you authorise us to charge your selected payment method at the start of each billing period.`
      },
      {
        subtitle: '4.3 Cancellation & Refunds',
        content: `You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period — you retain access until then. We do not offer refunds for partial billing periods. Exceptions may be made at our sole discretion for technical failures attributable to us.`
      },
      {
        subtitle: '4.4 Free Trial / Launch Offer',
        content: `During the promotional period (until April 10, 2026), launch pricing applies as displayed on the Pricing page. After the promotional period, standard rates apply and you will be notified in advance.`
      }
    ]
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use',
    content: `You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:`,
    items: [
      'Use the Service to create content that is illegal, harmful, abusive, defamatory, or infringes third-party rights.',
      'Attempt to reverse-engineer, scrape, or copy the AI models, design engine, or proprietary algorithms.',
      'Use automated bots, scripts, or tools to access the Service without our written permission.',
      "Attempt to gain unauthorised access to other users' accounts or data.",
      'Upload malicious code, viruses, or anything that could damage the Service or other users.',
      'Resell, sublicense, or commercially exploit the Service without a separate written agreement.',
      'Misrepresent your identity or impersonate another person or entity.',
    ]
  },
  {
    id: 'intellectual-property',
    title: '6. Intellectual Property',
    subsections: [
      {
        subtitle: '6.1 Your content',
        content: `You retain ownership of the event details and personal information you input into the Service. By using the Service, you grant VowVisionsAI a limited, non-exclusive licence to process your inputs solely for the purpose of generating your invitation designs.`
      },
      {
        subtitle: '6.2 Generated designs',
        content: `Upon generation, you receive a personal, non-exclusive licence to use, download, and print invitation designs generated through your account for personal or commercial event use. You may not resell generated designs as standalone templates or products without our written consent.`
      },
      {
        subtitle: '6.3 Our IP',
        content: `All design templates, AI models, software, branding, and platform code remain the exclusive intellectual property of VowVisionsAI. Nothing in these Terms transfers any IP rights to you beyond the limited licence described above.`
      }
    ]
  },
  {
    id: 'ai-disclaimer',
    title: '7. AI-Generated Content Disclaimer',
    content: `Our Service uses AI to generate invitation wording and designs. While we strive for quality and accuracy, AI-generated output may occasionally contain errors, unexpected phrasing, or design inconsistencies. You are responsible for reviewing all generated content before use. We make no warranty that AI output will be error-free or suitable for any particular purpose.`
  },
  {
    id: 'disclaimers',
    title: '8. Disclaimers & Limitation of Liability',
    content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VOWVISIONSAI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR DATA, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 3 MONTHS PRECEDING THE CLAIM.`
  },
  {
    id: 'termination',
    title: '9. Termination',
    content: `We may suspend or terminate your access to the Service at any time if you breach these Terms, if we reasonably suspect fraudulent activity, or if required by law. You may terminate your account at any time from your account settings. Upon termination, your right to use the Service ceases immediately. Provisions of these Terms that by their nature should survive termination will survive.`
  },
  {
    id: 'governing-law',
    title: '10. Governing Law & Dispute Resolution',
    content: `These Terms are governed by the laws of South Africa, without regard to conflict-of-law principles. For users in the EEA, mandatory EU consumer protection laws also apply and are not affected by this clause.

Any dispute arising from these Terms shall first be attempted to be resolved through good-faith negotiation. If unresolved within 30 days, disputes shall be submitted to binding arbitration in Cape Town, South Africa, except where prohibited by applicable law.`
  },
  {
    id: 'changes',
    title: '11. Changes to Terms',
    content: `We reserve the right to update these Terms at any time. We will provide at least 14 days' advance notice of material changes via email or in-app notification. Your continued use of the Service after changes take effect constitutes your acceptance. If you do not agree to the updated Terms, you must stop using the Service.`
  },
  {
    id: 'contact',
    title: '12. Contact',
    content: `For questions about these Terms, please contact us at ${CONTACT_EMAIL}. We aim to respond within 5 business days.`
  }
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FBF8F3] text-[#211D1A]">
      <section className="border-b border-[#E8E0D6] bg-white pb-16 pt-32 md:pt-36">
        <div className="mx-auto max-w-4xl px-6">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8A7A6A]">Legal</p>
          <h1 className="font-serif text-4xl leading-tight text-[#211D1A] md:text-5xl">Terms of Service</h1>
          <p className="mt-4 text-sm text-[#8A7A6A]">Last updated: {LAST_UPDATED}</p>
          <p className="mt-6 text-base leading-8 text-[#6C6258]">
            Please read these Terms carefully before using VowVisionsAI. They set out your rights, our responsibilities, and the rules that govern your use of our Service.
          </p>
          <div className="mt-10 flex flex-wrap gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-[#E4D9CB] bg-white px-4 py-1.5 text-xs font-medium text-[#6C6258] transition hover:border-[#C6A14B] hover:text-[#211D1A]"
              >
                {s.title.replace(/^\d+\.\s/, '')}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 space-y-12">
          {sections.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-28 rounded-[28px] border border-[#E8DDD1] bg-white p-8">
              <h2 className="font-serif text-2xl text-[#211D1A]">{s.title}</h2>
              {s.content && (
                <p className="mt-4 whitespace-pre-line text-sm leading-8 text-[#5E564F]">{s.content}</p>
              )}
              {s.subsections && s.subsections.map((sub) => (
                <div key={sub.subtitle} className="mt-6">
                  <h3 className="font-medium text-[#211D1A]">{sub.subtitle}</h3>
                  {sub.content && <p className="mt-2 whitespace-pre-line text-sm leading-7 text-[#5E564F]">{sub.content}</p>}
                  {sub.items && (
                    <ul className="mt-3 space-y-2">
                      {sub.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm leading-7 text-[#5E564F]">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C6A14B]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              {s.items && (
                <ul className="mt-4 space-y-2">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm leading-7 text-[#5E564F]">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C6A14B]" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="rounded-[28px] border border-[#E5D5BF] bg-[linear-gradient(180deg,#FFF9F1,#F8EEDF)] p-8 text-center">
            <p className="font-serif text-xl text-[#211D1A]">Have a question about our Terms?</p>
            <p className="mt-3 text-sm text-[#6C6258]">Email us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C6A14B] underline underline-offset-2">{CONTACT_EMAIL}</a></p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
              <Link to="/privacy" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Privacy Policy</Link>
              <span className="text-[#C6A14B]">·</span>
              <Link to="/cookies" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Cookie Policy</Link>
              <span className="text-[#C6A14B]">·</span>
              <Link to="/help" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Help Center</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
